import PropTypes from 'prop-types';
import { Text, Stack } from '@etg/wings';
import { useTranslation, useProperty } from '@eti/providers';
import { AncillaryCarrierPriceBreakdown, BoundNew } from '@eti/types';
import { getExtraProducts } from '../utils';
import { hasRyanAirTicketingOrMarketingCarrier } from '../../../../../utils/carriersUtils';
import { getUniqueMarketingCarriers } from '../../../../../utils/carriers';
import CartTravelerProducts from './CartTravelerProducts';
import CartExtraProducts from './CartExtraProducts';
import TotalServiceFeeRyanair from './TotalServiceFeeRyanair';

const CartTravelerInfo = ({
  bounds,
  bundles,
  includedExtraProducts,
  marketingCarrierNames,
  products,
  serviceFee,
  travelers,
}) => {
  const { t } = useTranslation();
  const { p } = useProperty();

  const extraProducts = getExtraProducts(products);
  const uniqueCarriers = getUniqueMarketingCarriers(bounds);
  const isRyanairInTrip = hasRyanAirTicketingOrMarketingCarrier(
    p('IbeClient.RyanairMarketingCarriers'),
    bounds,
  );

  return (
    <Stack>
      <Text as="p" variant="18-emphasized">
        {t('Cart.PriceDetails.Title')}
      </Text>
      <CartTravelerProducts
        includedExtraProducts={includedExtraProducts}
        isRyanairInTrip={isRyanairInTrip}
        marketingCarrierNames={marketingCarrierNames}
        travelers={travelers}
        uniqueCarriers={uniqueCarriers}
      />
      <CartExtraProducts bundles={bundles} extraProducts={extraProducts} />
      {isRyanairInTrip && (
        <TotalServiceFeeRyanair products={products} serviceFee={serviceFee} travelers={travelers} />
      )}
    </Stack>
  );
};

CartTravelerInfo.propTypes = {
  bounds: PropTypes.arrayOf(BoundNew),
  bundles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      optionId: PropTypes.string,
      price: PropTypes.number,
      products: PropTypes.arrayOf(PropTypes.object),
      title: PropTypes.string,
    }),
  ),
  includedExtraProducts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      texts: PropTypes.shape({
        name: PropTypes.string,
      }),
    }),
  ),
  marketingCarrierNames: PropTypes.string,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      AncillaryCarrierPriceBreakdowns: PropTypes.arrayOf(AncillaryCarrierPriceBreakdown),
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      markup: PropTypes.number,
      name: PropTypes.string,
      numberOfUnits: PropTypes.number,
      price: PropTypes.number,
      title: PropTypes.string,
    }),
  ),
  serviceFee: PropTypes.shape({
    currency: PropTypes.shape({
      code: PropTypes.string,
      exponent: PropTypes.number,
      name: PropTypes.string,
    }),
    value: PropTypes.number,
  }),
  travelers: PropTypes.arrayOf(
    PropTypes.shape({
      ageType: PropTypes.string,
      firstName: PropTypes.string,
      fullName: PropTypes.string,
      lastName: PropTypes.string,
      markup: PropTypes.number,
      price: PropTypes.number,
      products: PropTypes.arrayOf(PropTypes.any),
      quantity: PropTypes.number,
      taxesAndFees: PropTypes.arrayOf(PropTypes.any),
      totalPrice: PropTypes.number,
      totalPriceSum: PropTypes.number,
      vat: PropTypes.number,
    }),
  ),
};

export default CartTravelerInfo;
